import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SyncIcon from "@mui/icons-material/Sync";
import HelpIcon from "@mui/icons-material/Help";

export const Health: React.FC = () => {
  const [status, setStatus] = useState<string>("Loading...");
  const [loading, setLoading] = useState<boolean>(false);
  const [deploying, setDeploying] = useState<boolean>(false);
  const [permitted, setPermitted] = useState<boolean>(
    !!localStorage.getItem("pt_health_permitted")
  );

  const token = process.env.REACT_APP_DIGITALOCEAN_TOKEN;
  const appId = "76de324a-8c7d-4fee-b9a0-9cc9094d087a";

  useEffect(() => {
    if (!permitted) {
      const askPassword = () => {
        const pass = prompt("Enter password for app health:");
        if (pass === process.env.REACT_APP_DIGITALOCEAN_HEALTH_PASS) {
          localStorage.setItem("pt_health_permitted", "true");
          setPermitted(true);
        } else {
          // Prompt again after a short delay so React isn't blocked
          setTimeout(askPassword, 100);
        }
      };
  
      askPassword();
    }
  }, [permitted]);


  // --- Fetch App Status ---
  async function fetchAppStatus() {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.digitalocean.com/v2/apps/${appId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const app = response.data.app;
      const state =
        app.active_deployment?.phase ||
        app.pending_deployment?.phase ||
        "unknown";
      setStatus(state.toLowerCase());
    } catch (error: any) {
      console.error(
        "Failed to fetch app status:",
        error.response?.data || error.message
      );
      setStatus("error_fetching_status");
    } finally {
      setLoading(false);
    }
  }

  // --- Trigger Deployment ---
  async function triggerDeployment() {
    setDeploying(true);
    try {
      const response = await axios.post(
        `https://api.digitalocean.com/v2/apps/${appId}/deployments`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Deployment triggered successfully!", response.data);
      setStatus("deploying");
    } catch (error: any) {
      console.error(
        "Failed to trigger deployment:",
        error.response?.data || error.message
      );
      setStatus("failed_to_deploy");
    } finally {
      setDeploying(false);
    }
  }

  // Fetch app status on mount
  useEffect(() => {
    if (!permitted) return;
    fetchAppStatus();
    const interval = setInterval(fetchAppStatus, 30000);
    return () => clearInterval(interval);
  }, [permitted]);

  // --- Map statuses to colors and icons ---
  const getStatusInfo = (phase: string) => {
    switch (phase) {
      case "active":
        return {
          label: "Active — Running",
          color: "success",
          icon: <CheckCircleIcon color="success" fontSize="large" />,
        };
      case "deploying":
      case "building":
      case "pending_build":
        return {
          label: "Deploying...",
          color: "warning",
          icon: <SyncIcon color="warning" fontSize="large" />,
        };
      case "error":
      case "failed_to_deploy":
      case "error_fetching_status":
        return {
          label: "Error — App Unhealthy",
          color: "error",
          icon: <ErrorIcon color="error" fontSize="large" />,
        };
      default:
        return {
          label: "Unknown Status",
          color: "default",
          icon: <HelpIcon color="disabled" fontSize="large" />,
        };
    }
  };

  const { label, color, icon } = getStatusInfo(status);

  if (!permitted) return null;
  return (
    <Card
      sx={{
        maxWidth: 420,
        m: 4,
        p: 3,
        margin: "auto",
        marginTop: "100px",
        textAlign: "center",
        borderRadius: 3,
        boxShadow: 3,
      }}
    >
      <CardContent>
        <Typography variant="h5" gutterBottom fontWeight="bold">
        App Health
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 3 }}>
            {icon}
            <Chip
              label={label}
              color={color as any}
              sx={{ fontSize: "1rem", fontWeight: "bold", px: 1.5, py: 1 }}
            />
          </Box>
        )}

        <Stack direction="column" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={triggerDeployment}
            disabled={deploying}
          >
            {deploying ? "Deploying..." : "Trigger Deployment"}
          </Button>

          <Button variant="outlined" onClick={fetchAppStatus}>
            Refresh Status
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
